import { useEffect, useState } from "react";
import * as St from './index.styled';
import CustomBlockButton from "../../../components/button/CustomBlockButton";
import axios from "axios";
import { customToast } from "../../../components/toast/custom-react-toastify/v1";
import { CustomBackdropController } from "../../../components/backdrop/default/v1";
import { useApiInfoContextValueHook } from "../../../contexts/ApiInfoContextProvider";
import { SellertoolSignatureUtils } from "../../../utils/sellertool-signature-utils";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { CustomDialog } from "../../../components/dialog/v1/CustomDialog";
import { CustomDateUtils } from "../../../utils/custom-date-utils";
import { useQueryParamsHook } from "../../../hooks/useQueryParamsHook";

const GOOGLE_SHEETS_API_KEY = 'AIzaSyA8P2TGBs5yj0oDRPd3k2Z_iyaOH2hly2M';
const API_HOST = process.env.REACT_APP_MAIN_API_HOST;

export function MainView(props) {
    const navigate = useNavigate();
    const queryParamsHook = useQueryParamsHook();
    const currentStageUUID = queryParamsHook?.params?.stage;

    const apiInfoContextValueHook = useApiInfoContextValueHook();

    const [currentStage, setCurrentStage] = useState(null);
    const [mode, setMode] = useState('REGISTER') // REGISTER, LIST
    const [itemList, setItemList] = useState(null);

    useEffect(() => {
        if (currentStageUUID) {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            });

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            };

            axios.get(`${API_HOST}/inbound-stages/uuid/${currentStageUUID}`, {
                headers: headers
            })
                .then(res => {
                    if (res.status === 200 && res.data?.content) {
                        setCurrentStage(res.data.content);
                    }
                })
                .catch(err => {
                    customToast.error(err.response);
                });
        }
    }, [currentStageUUID]);

    const handleChangeMode = (newMode) => {
        setMode(newMode);
    }

    const handleSetItemList = (values) => {
        setItemList(values);
    }

    const handleRefresh = () => {
        setItemList(null);
        setMode('REGISTER');
    }

    const handleRegister = async () => {
        const timestamp = Date.now().toString();
        const signature = SellertoolSignatureUtils().generateSigniture({
            apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
            secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
            timestamp: timestamp
        })

        const headers = {
            'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
            'x-sellertool-timestamp': timestamp,
            'x-sellertool-signiture': signature
        }

        const body = {
            warehousingItems: itemList
        }
        const registerResult = await axios.post(`${API_HOST}/warehousing-items/register`, body, {
            headers: headers
        })
            .then(res => {
                return {
                    res: res,
                    content: res.data?.content
                }
            })
            .catch(err => {
                customToast.error(err.response);
            });

        if (registerResult?.content) {
            const queryParams = queryParamsHook.params;
            const queryString = new URLSearchParams(queryParams).toString();
            const fullPath = `/inbound/in-progress?${queryString}`;

            navigate(fullPath);
        }
    }

    return (
        <>
            <StageSelection
                currentStage={currentStage}
            />
            {currentStage &&
                <>
                    {mode === 'REGISTER' &&
                        <RegisterMode
                            onChangeMode={handleChangeMode}
                            onSetItemList={handleSetItemList}
                            currentStage={currentStage}
                        />
                    }
                    {mode === 'LIST' &&
                        <ListMode
                            itemList={itemList}
                            onRefresh={handleRefresh}
                            onRegister={handleRegister}
                        />
                    }
                </>
            }
        </>
    );
}

function RegisterMode({
    onChangeMode,
    onSetItemList,
    currentStage
}) {
    const handleLoadDataWithGoogleSheets = async () => {
        const spreadsheetId = '1Gg7qnMrWHS92RM5P_DS95kT0uh3O-dIOJApaKyFij1I';
        const sheetName = '입고프로세스_불러오기용';

        CustomBackdropController().showBackdrop();
        const sheetItemList = await axios.get(`https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${sheetName}?key=${GOOGLE_SHEETS_API_KEY}`)
            .then(res => {
                if (res.status === 200 && res.data?.values && res.data?.values?.length > 0) {
                    const values = res.data.values;
                    const itemList = [];
                    values.forEach((value, index) => {
                        if (index === 0) {
                            return;
                        }

                        itemList.push({
                            uuid: uuidv4(),
                            waybillNumber: value[0]?.trim(),
                            mergingCode: value[1]?.trim(),
                            optionCode: value[2]?.trim(),
                            warehousingProductName: value[3]?.trim(),
                            warehousingOptionName: value[4]?.trim(),
                            productQuantity: value[5]?.trim(),
                            boxQuantity: value[6]?.trim(),
                            boxWidth: value[7] * 10,
                            boxDepth: value[8] * 10,
                            boxHeight: value[9] * 10,
                            boxCBM: (value[7] * value[8] * value[9]) / 1000000,
                            stageUUID: currentStage.uuid
                        })
                    })
                    return itemList
                }
            })
            .catch(err => {
                console.log(err);
                customToast.error(`스프레드시트 불러오기에 실패 했습니다.\n${err}`)
            })

        onSetItemList(sheetItemList);
        onChangeMode('LIST');
        CustomBackdropController().hideBackdrop();
    }

    return (
        <St.RegisterButtons.Container>
            <CustomBlockButton
                type='button'
                onClick={() => handleLoadDataWithGoogleSheets()}
            >
                스프레드시트 불러오기
            </CustomBlockButton>
            <CustomBlockButton
                type='button'
                disabled
            >
                엑셀 파일 업로드
            </CustomBlockButton>
            <CustomBlockButton
                type='button'
                disabled
            >
                수기 등록
            </CustomBlockButton>
        </St.RegisterButtons.Container>
    );
}

function ListMode({
    itemList,
    onRefresh,
    onRegister
}) {
    const productCount = itemList?.length;
    const totalCTNCount = itemList?.reduce((prev, current) => (Number(prev) + Number(current?.boxQuantity)), 0);
    return (
        <>
            <St.ListMode.Container>
                <St.ListMode.CountBox>
                    <div>
                        총 CTN 수 : {totalCTNCount}
                    </div>
                    <div>
                        총 상품수 : {productCount}
                    </div>
                </St.ListMode.CountBox>
                {itemList?.map((item, index) => {
                    return (
                        <St.ListMode.ItemBox
                            key={index}
                        >
                            <div className='productName'>
                                <div>
                                    {item?.warehousingProductName} / {item?.warehousingOptionName}
                                </div>
                                <div className='ctns'>{item?.boxQuantity} CTNS</div>
                            </div>
                            <div className='detailInfo'>
                                {item?.productQuantity} 개 / 운송장: {item?.waybillNumber}
                            </div>
                        </St.ListMode.ItemBox>
                    );
                })}
                <St.ListMode.FooterBox>
                    <CustomBlockButton
                        type='button'
                        className='refresh'
                        onClick={() => onRefresh()}
                    >
                        새로고침
                    </CustomBlockButton>
                    <CustomBlockButton
                        type='button'
                        onClick={() => onRegister()}
                    >
                        등록
                    </CustomBlockButton>
                </St.ListMode.FooterBox>
            </St.ListMode.Container>
        </>
    );
}

const StageSelection = ({
    currentStage
}) => {
    const apiInfoContextValueHook = useApiInfoContextValueHook();

    const queryParamsHook = useQueryParamsHook();

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [stageList, setStageList] = useState(null);

    const openModal = () => {
        setModalIsOpen(true);
        if (stageList === null) {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            })

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            }

            axios.get(`${API_HOST}/inbound-stages`, {
                headers: headers
            })
                .then(res => {
                    if (res.status === 200 && res.data?.content) {
                        setStageList(res.data.content);
                    }
                })
                .catch(err => {
                    customToast.error(err.response);
                });
        }
    };

    const closeModal = () => {
        setModalIsOpen(false);
    };

    const handleStageSelect = (stage) => {
        queryParamsHook.setSearchParams({ stage: stage.uuid });
        closeModal();
    };

    return (
        <St.StageSelection.Container>
            <St.StageSelection.SelectionButton
                type='button'
                onClick={openModal}
            >
                {currentStage ?
                    <div>
                        <div>
                            ({currentStage?.name})
                        </div>
                        <div>
                            {currentStage?.uuid}
                        </div>
                    </div>
                    :
                    <div>
                        스테이지 선택
                    </div>
                }
            </St.StageSelection.SelectionButton>
            <CustomDialog
                open={modalIsOpen}
                onClose={closeModal}
            >
                <St.StageSelection.DialogHeader>
                    <CustomDialog.Header>
                        <CustomDialog.Header.Fake></CustomDialog.Header.Fake>
                        <CustomDialog.Header.Title>스테이지 선택</CustomDialog.Header.Title>
                        <CustomDialog.Header.Close onClick={closeModal}></CustomDialog.Header.Close>
                    </CustomDialog.Header>
                </St.StageSelection.DialogHeader>
                <St.StageSelection.StageList>
                    {stageList?.map((stage, index) => {
                        return (
                            <St.StageSelection.StageItem
                                key={index}
                                onClick={() => handleStageSelect(stage)}
                            >
                                <div className='uuid'>
                                    ({stage.name})<br />{stage.uuid}
                                </div>
                                <div className='createdAt'>
                                    생성일 : {CustomDateUtils().formatDateToYYYYMMDD_HHMMSSFromUnixTime(stage.createdAt)}
                                </div>
                            </St.StageSelection.StageItem>
                        );
                    })}
                </St.StageSelection.StageList>
            </CustomDialog>
        </St.StageSelection.Container>
    );
};